import { match } from "ts-pattern";
import type {
  ElevenLabsConversationAdapter,
  ElevenLabsEndResult,
  ElevenLabsStartOptions,
} from "../hooks/useElevenLabsConversation";

type ConversationInterface = Pick<
  ElevenLabsConversationAdapter,
  "startSession" | "endSession" | "sendUserMessage"
>;

type ConnectionState =
  | { type: "disconnected" }
  | { type: "connecting" }
  | { type: "connected"; sessionId: string }
  | { type: "error"; error: Error };

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

class ConversationService {
  private conversation: ConversationInterface;
  private state: ConnectionState = { type: "disconnected" };

  constructor(conversation: ConversationInterface) {
    this.conversation = conversation;
  }

  async startSession(options: ElevenLabsStartOptions): Promise<string> {
    const canStart = match(this.state)
      .with({ type: "disconnected" }, () => true)
      .with({ type: "error" }, () => true) // Allow retry on error
      .otherwise(() => false);

    if (!canStart) {
      throw new Error(
        `Cannot start session in current state: ${this.state.type}`
      );
    }

    try {
      this.state = { type: "connecting" };
      const sessionId = await this.conversation.startSession({
        ...options,
        clientTools: options.clientTools ?? {},
      });

      this.state = { type: "connected", sessionId };
      return sessionId;
    } catch (error) {
      this.state = { type: "error", error: toError(error) };
      throw error;
    }
  }

  async endSession(): Promise<ElevenLabsEndResult> {
    // Не проверяем внутренний state - всегда пытаемся остановить
    // SDK сам корректно обработает вызов в любом состоянии
    // Это предотвращает рассинхронизацию между нашим state и реальным conversation.status

    try {
      const result = await this.conversation.endSession();
      // Only set state to disconnected on success
      this.state = { type: "disconnected" };
      return result;
    } catch (error) {
      console.error("[ConversationService] Error ending session:", error);
      this.state = { type: "error", error: toError(error) };
      // Re-throw the error so the caller knows it failed
      throw error;
    }
  }

  sendUserMessage(text: string): void {
    if (this.state.type !== "connected") {
      throw new Error("No active conversation session");
    }
    this.conversation.sendUserMessage(text);
  }

  isSessionActive(): boolean {
    return this.state.type === "connected";
  }

  static create(conversation: ConversationInterface): ConversationService {
    return new ConversationService(conversation);
  }
}

export default ConversationService;
export type { ConversationInterface };
